/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import { Form, Formik } from "formik";
import { HelpPopup } from "../../components/helpPopup/helpPopup";
import * as Yup from "yup";
import { MessageText } from "../../constants/messageText";
import { Box, Button, Chip, CircularProgress, Divider } from "@mui/material";
import { TextBox } from "../../components/allInputsFields/textBox/textBox";
import "./signUpTrial.scss";
import instance from "../../utils/axiosInstance";
import React, { useEffect, useState } from "react";
import { ErrorCodes } from "../../constants/messageCodes";
import { SnackBarMessage } from "../../components/snackBar/snackBar";
import { SignUpSucees } from "../../components/signUpSucees/signUpSucees";
import { useNavigate } from "react-router-dom";
import { CheckSubscriptionStatus, CreateUser } from "../../constants/api-urlConstants";
import { TermsPopup } from "../../components/termsPopup/termsPopup";
import { CheckBoxOutlineBlank, Done } from "@mui/icons-material";
import { PRIVACY_POLICY_URL } from "../../utils/constant";
import { getPrivacyPolicyTitle, getPrivacyPolicyUrl } from "../../utils";
import TrialStepper from "./trialStepper";
import EmbeddedCheckout from "./embeddedCheckout";
import { decryptAndParsePAYGData } from "../../constants/commonConstants";
import { apiBasePath } from "../../constants/env-url";
import SigninWithGoogleButton from "../../components/signinWithGoogleButton";

export const SignUpForTrial: React.FC<any> = () => {
  
  const navigate = useNavigate();
  const param = new URLSearchParams(window.location.search);
  const access = param.get('access');
  const encryptedUserInfo = param.get('data');
  const [guestSignIsLoading, setGuestSignIsLoading] = React.useState(false);
  const [showInfromation, setShowInfromation] = React.useState<any>(false);
  const [showTermsPopup, setShowTermsPopup] = React.useState({open: false, type: ""});
  const userInfo: any = encryptedUserInfo && decryptAndParsePAYGData((encryptedUserInfo) as string) || {};
  const [userExistingInfo, setUserExistingInfo] = useState<any>();
  const [stepperInfo, setStepper] = React.useState({ activeStep: 1, userData: userInfo ? userInfo : {}});
  const [APIMsgConfig, setAPIMsgConfig] = React.useState({
    type: "",
    message: "",
    snackFlag: false,
  });

  const initial_state = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    termsOfUse: false,
    privacy: false,
    // salesAgreement: false,
    referredUser: false,
  };

  (window as any).dataPopupWebhookReceived = () => {
    setTimeout(() => {
      setStepper(old => ({ ...old, activeStep: 3 }));
    }, 5000);
  };

  const onlyText = /^[A-Za-z0-9 ]+$/;
  const noFreeMailBoxes =/^[a-zA-Z0-9_.+-]+@(?!(yahoo|hotmail|aol)).*\.[a-zA-Z]{2,100}$/gm;

  const SigninFormValidation = Yup.object().shape({
    firstName: Yup.string()
      .min(2, MessageText.VALIDATION_MIN_2)
      .required(MessageText.VALIDATION_FIRSTNAME_REQUIRED)
      .trim(MessageText.VALIDATION_FIRSTNAME_REQUIRED)
      .matches(onlyText, MessageText.SPECIAL_CHARACTERS_ARE_NOT_ALLOWED),
    lastName: Yup.string()
      .min(1, MessageText.VALIDATION_MIN_2)
      .required(MessageText.VALIDATION_LASTNAME_REQUIRED)
      .matches(onlyText, MessageText.SPECIAL_CHARACTERS_ARE_NOT_ALLOWED)
      .trim(MessageText.VALIDATION_LASTNAME_REQUIRED),
    email: Yup.string()
      .email(MessageText.VALIDATION_EMAIL_FORMAT)
      .matches(noFreeMailBoxes, MessageText.VALIDATION_EMAIL_DOMAIN)
      .required(MessageText.VALIDATION_EMAIL_REQUIRED),
    company: Yup.string()
      .min(1, MessageText.VALIDATION_MIN_2)
      .required(MessageText.VALIDATION_COMPANY_REQUIRED)
      .trim(MessageText.VALIDATION_COMPANY_REQUIRED),
    privacy: Yup.boolean().oneOf(
      [true],
      "You need to accept the Privacy Policy"
    ),
    termsOfUse: Yup.boolean().oneOf(
      [true],
      "You need to accept the terms and conditions"
    ),
    // salesAgreement: Yup.boolean().oneOf(
    //   [true],
    //   "You need to accept the Sales Agreement"
    // ),
  });

  useEffect(() => {
    if (userInfo?.email) {
      const api_url = `${apiBasePath}${CheckSubscriptionStatus}`;
      const payload = { email: userInfo?.email };
      instance.post(api_url, payload).then((res) => {
        setUserExistingInfo(res?.data?.data);
      });
    }
  }, [])

  useEffect(() => {
    if (access && !encryptedUserInfo) {
      setStepper(old => ({...old, activeStep: 1}));
    } else if (access && encryptedUserInfo) {
      setStepper(old => ({...old, activeStep: 2}));
    }
  }, [])

  const createTrialUser = (userData: any) => {
    setGuestSignIsLoading(true);
    const payload: any = {
      firstName: userData?.firstName?.trim?.(),
      lastName: userData?.lastName?.trim?.(),
      email: userData?.email,
      company: userData?.company,
      referredUser: userData?.referredUser,
      role: access?.toLowerCase() === 'pro' ? 'pro' : 'basic',
    };
    const USER_API_BASE_URL = `${CreateUser}`;
    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        const res = response.data;
        setGuestSignIsLoading(false);
        if (res.success) {
          setStepper(old => ({activeStep: old.activeStep + 1, userData}));
        } else {
          if (res.message_code === ErrorCodes.DUPLICATE_USERNAME) {
            setAPIMsgConfig({
              type: 'error',
              message: MessageText.EMAIL_ALREADY_EXIST,
              snackFlag: true
            })
          } else if (res.message_code === ErrorCodes.INVALID_EMAIL) {
            setAPIMsgConfig({
              type: 'error',
              message: MessageText.EMAIL_FORMAT_INVALID,
              snackFlag: true
            })
          } else if (res.message_code === ErrorCodes.USER_ALREADY_EXIST && (res.data && !res?.data?.subscriptionStatus)) {
            setStepper(old => ({activeStep: old.activeStep + 1, userData}));
          } else if (res.message_code === ErrorCodes.USER_ALREADY_EXIST) {
            setAPIMsgConfig({
              type: 'error',
              message: MessageText.USER_ALREADY_EXIST,
              snackFlag: true
            })
          } else {
            setAPIMsgConfig({
              type: 'error',
              message: MessageText.WENT_WRONG,
              snackFlag: true
            })
          }
        }
      })
      .catch((error) => {
        setGuestSignIsLoading(false);
        setAPIMsgConfig({
          type: 'error',
          message: MessageText.WENT_WRONG,
          snackFlag: true
        })

      });
  };

  const okClick = () => {
    navigate("/login");
  };

  const handleTermsClick = (type: string) => {
    setShowTermsPopup({open: true, type: type});
  };

  const handleCloseTermsPopup = () => {
    setShowTermsPopup({open: false, type: ""});
  };

  return (
    <>
      <div className="guest-signUp-wrapper flex-direction-column">
        <div className="pageHeader vertical-align">
          <img
            style={{ width: "220px", paddingLeft: "10px" }}
            alt=""
            src={require("../../assets/images/infraclearlogo.png")}
          />
          <HelpPopup />
        </div>
        {(!userExistingInfo?.hasActiveSubscription) && access &&
          <TrialStepper stepperInfo={stepperInfo} />
        }
        <Box sx={{width: '100%', minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
          {((userInfo?.email && !userExistingInfo?.hasActiveSubscription) || (!userInfo?.email)) ?
            <>
              {(stepperInfo.activeStep === 1) &&
                <div className="login-wrapper trial-signup">
                  {!showInfromation && (
                    <div className="login-popup">
                      <div className="loginWrapper">
                        <span className="title">Create Account</span>
                        <div className="titleWrapper">
                          <span className="subTitle">Already have account?</span>
                          <span className="signText" onClick={okClick}>Sign in</span>
                        </div>
                        <Formik
                          initialValues={initial_state}
                          validationSchema={SigninFormValidation}
                          onSubmit={(value: any) => {
                            createTrialUser(value);
                          }}
                        >
                          {(formik) => (
                            <Form onSubmit={formik.handleSubmit}>
                              <Box sx={{ mt: 2 }}>
                                <TextBox
                                  formikRef={formik}
                                  textBoxName={"firstName"}
                                  textBoxLabel={"First Name *"}
                                  alphaNumaricInputText={true}
                                  inputPropsObj={{
                                    maxLength: 25,
                                    spellCheck: false,
                                  }}
                                  margin={"dense"}
                                  onChangeCallBack={(data: any) => {
                                    formik.setFieldValue("firstName", data);
                                  }}
                                />
                                <TextBox
                                  formikRef={formik}
                                  textBoxName={"lastName"}
                                  textBoxLabel={"Last Name *"}
                                  alphaNumaricInputText={true}
                                  inputPropsObj={{
                                    maxLength: 25,
                                    spellCheck: false,
                                  }}
                                  margin={"dense"}
                                  onChangeCallBack={(data: any) => {
                                    formik.setFieldValue("lastName", data);
                                  }}
                                />
                                <TextBox
                                  formikRef={formik}
                                  textBoxName={"company"}
                                  textBoxLabel={"Company *"}
                                  alphaNumaricInputText={true}
                                  inputPropsObj={{
                                    maxLength: 25,
                                    spellCheck: false,
                                  }}
                                  margin={"dense"}
                                  onChangeCallBack={(data: any) => {
                                    formik.setFieldValue("company", data);
                                  }}
                                />
                                <TextBox
                                  formikRef={formik}
                                  textBoxName={"email"}
                                  inputPropsObj={{ spellCheck: false }}
                                  textBoxLabel={"Email *"}
                                  margin={"dense"}
                                  onChangeCallBack={(data: any) => {
                                    formik.setFieldValue("email", data);
                                  }}
                                />
                                <Box sx={{ fontSize: 14, color: '#666666' }}>Please review and accept the following agreements to continue.</Box>
                                <Box sx={{ display:'flex', gap: 2, mt: 1, justifyContent: 'space-between'}}>
                                  <Button fullWidth onClick={() => handleTermsClick(PRIVACY_POLICY_URL.TERMS_OF_USE)} color={formik.values.termsOfUse ? "success" : "primary"} variant={"outlined"} size="small" startIcon={formik.values.termsOfUse ? <Done /> : <CheckBoxOutlineBlank />} sx={{ textTransform: "none", justifyContent: 'flex-start' }}>
                                    Terms Of Use
                                  </Button>
                                  <Button fullWidth onClick={() => handleTermsClick(PRIVACY_POLICY_URL.PRIVACY_POLICY)} color={formik.values.privacy ? "success" : "primary"} variant="outlined" size="small" startIcon={formik.values.privacy ? <Done /> : <CheckBoxOutlineBlank />} sx={{ textTransform: "none", justifyContent: 'flex-start' }}>
                                    Privacy Policy
                                  </Button>
                                  {/* <Button onClick={() => handleTermsClick(PRIVACY_POLICY_URL.SALES_AGREEMENT)} color={formik.values.salesAgreement ? "success" : "primary"} variant="outlined" size="small" startIcon={formik.values.salesAgreement ? <Done /> : <CheckBoxOutlineBlank />} sx={{ textTransform: "none", justifyContent: 'space-between' }}>
                                    Sales Agreement
                                  </Button> */}
                                </Box>
                                <div className="loader">
                                  <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, mb: 2 }}
                                    style={{ textTransform: "none" }}
                                    className={
                                      !(formik.isValid && formik.dirty)
                                        ? "signupBtnDisabled"
                                        : "signupBtn"
                                    }
                                    disabled={
                                      !(formik.isValid && formik.dirty) ||
                                      guestSignIsLoading
                                    }
                                  >
                                    {!guestSignIsLoading ? (
                                      "Create account"
                                    ) : (
                                      <div className="submitFormLoading">
                                        <CircularProgress color="inherit" size={20} />
                                      </div>
                                    )}
                                  </Button>
                                  
                                  <Divider>
                                    <Chip label="Or" size="small" />
                                  </Divider>
                                  <SigninWithGoogleButton />
                                </div>
                              </Box>
                              <TermsPopup
                                open={showTermsPopup.open}
                                onClose={handleCloseTermsPopup}
                                url={getPrivacyPolicyUrl(showTermsPopup.type)}
                                title={getPrivacyPolicyTitle(showTermsPopup.type)}
                              />
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </div>
                  )}
                </div>
              }
              {stepperInfo.activeStep === 2 &&
                <EmbeddedCheckout userData={stepperInfo.userData} access={access?.toLowerCase()} />
              }
              {stepperInfo.activeStep === 3 &&
                <SignUpSucees closeConfirmation={okClick} showText={MessageText.RESET_LINK_TO_MAIL} popupName={'signUp'} />
              }
            </>
            :
            <>
              <SignUpSucees closeConfirmation={okClick} showText={MessageText.ACCOUNT_ALREADY_REGISTERED} popupName={'signUp'} />
            </>
          }
        </Box>
      </div>
      {APIMsgConfig.snackFlag && (
        <div>
          <SnackBarMessage snacBarValue={APIMsgConfig} />
        </div>
      )}
    </>
  );
};
